import type {
  AddOrUpdateQuestionSetSavePayload,
  EditableGradingRubric,
  EditableQuestion,
  EditableQuestionImage,
  EditableQuestionPart,
  EditableVariable,
  EditorState,
  RawAnswer,
  RawContentSection,
  RawQuestion,
  RawVariable,
} from './editorTypes';
import { buildPromptContentUnits } from './promptContentUnits';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function cloneValue<T>(value: T): T {
  if (typeof structuredClone === 'function') {
    return structuredClone(value);
  }

  return JSON.parse(JSON.stringify(value)) as T;
}

function normalizeString(value: unknown): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function normalizeNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  const normalized = normalizeString(value);

  if (!normalized) {
    return undefined;
  }

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function toSnakeCaseKey(value: string) {
  return value.replace(/[A-Z]/g, (match) => `_${match.toLowerCase()}`);
}

function toSnakeCaseValue<T>(value: T): T {
  if (Array.isArray(value)) {
    return value.map((entry) => toSnakeCaseValue(entry)) as T;
  }

  if (!isRecord(value)) {
    return value;
  }

  return Object.entries(value).reduce<Record<string, unknown>>((result, [key, entry]) => {
    result[toSnakeCaseKey(key)] = toSnakeCaseValue(entry);
    return result;
  }, {}) as T;
}

function getQuestionTypeEnum(
  type: EditableQuestion['type'] | EditableQuestionPart['type'],
) {
  if (type === 'AI_UPLOAD') {
    return 6;
  }

  if (type === 'NUMERICAL_WITH_ERROR') {
    return 3;
  }

  if (type === 'EXACT_ANSWER') {
    return 4;
  }

  if (type === 'MULTI_PART') {
    return 5;
  }

  if (type === 'SELECT_ALL') {
    return 2;
  }

  return 1;
}

function serializeGradingRubric(
  rubric: EditableGradingRubric | undefined,
) {
  const rubricUnits = (rubric?.rubricUnits ?? []).map((unit) => ({
    details: normalizeString(unit.details) ?? '',
    name: normalizeString(unit.name) ?? '',
    score: normalizeNumber(unit.score) ?? 0,
  }));
  const totalScore =
    rubricUnits.reduce((total, unit) => total + unit.score, 0);

  return {
    rubric_units: rubricUnits,
    total_score: totalScore,
  };
}

function buildTextContentSection(
  html: string | undefined,
  sourceSection: RawContentSection | null | undefined,
  images: EditableQuestionImage[] = [],
): RawContentSection {
  const nextHtml = html ?? '<p></p>';
  const baseSection = sourceSection ? toSnakeCaseValue(cloneValue(sourceSection)) : {};

  // Rebuild the units from the edited text and the (edited) prompt images, so
  // image edits/deletions/additions round-trip. Collapsing to a single text unit
  // here silently deleted images on save.
  return {
    ...(baseSection as RawContentSection),
    content_units: buildPromptContentUnits(
      nextHtml,
      images,
      baseSection as RawContentSection,
    ),
  };
}

function buildOptionalTextContentSection(
  html: string | undefined,
  sourceSection: RawContentSection | null | undefined,
  images: EditableQuestionImage[] = [],
) {
  const normalized = normalizeString(html);

  if (!normalized && !sourceSection && images.length === 0) {
    return undefined;
  }

  return buildTextContentSection(html ?? '<p></p>', sourceSection, images);
}

function buildContentOptions(
  options: string[],
  sourceQuestion: RawQuestion | null | undefined,
) {
  const rawContentOptions = Array.isArray(
    sourceQuestion?.content_options ?? sourceQuestion?.contentOptions,
  )
    ? ((sourceQuestion?.content_options ??
        sourceQuestion?.contentOptions) as RawContentSection[])
    : [];

  return options.map((option, index) =>
    buildTextContentSection(option, rawContentOptions[index]),
  );
}

function serializeVariables(
  variables: EditableVariable[],
  sourceVariables: RawVariable[] | null | undefined,
) {
  return variables.map((variable, index): RawVariable => {
    const baseVariable = toSnakeCaseValue(cloneValue(sourceVariables?.[index] ?? {}));

    return {
      ...(baseVariable as RawVariable),
      max_value: variable.maxValue,
      min_value: variable.minValue,
      name: variable.name,
      type: 1,
    };
  });
}

// `current_answer` and `current_num_attempts` describe a viewer, not the
// question — and the question is the definition every viewer is served. A
// loaded question can carry them (the retired frontend saved its viewer's
// selection with the chapter), and this used to send them straight back, so a
// stored selection survived every edit.
//
// Multiple choice gets the no-selection marker this editor has always written
// on a new question, whatever was loaded. It cannot simply be dropped: the
// question bank hashes a question as sent to decide whether it has changed,
// so the marker has to be the same from one save to the next. Every other
// type carries no current answer.
function withoutViewerState(
  question: RawQuestion,
  type: EditableQuestion['type'] | EditableQuestionPart['type'],
): RawQuestion {
  delete (question as RawQuestion & { current_num_attempts?: unknown })
    .current_num_attempts;

  if (type === 'MULTIPLE_CHOICE') {
    question.current_answer = { answer_index: [-1] } satisfies RawAnswer;
  } else {
    delete (question as RawQuestion & { current_answer?: unknown }).current_answer;
  }

  return question;
}

// A question of a type this editor does not edit is sent as it was loaded,
// minus the same two fields, at every level.
function dropViewerState(question: RawQuestion): RawQuestion {
  delete (question as RawQuestion & { current_answer?: unknown }).current_answer;
  delete (question as RawQuestion & { current_num_attempts?: unknown })
    .current_num_attempts;

  const parts = question.question_set?.questions;

  if (Array.isArray(parts)) {
    parts.forEach((part) => {
      if (isRecord(part)) {
        dropViewerState(part);
      }
    });
  }

  return question;
}

function stripNestedIdentityFields(question: RawQuestion) {
  delete (question as RawQuestion & { author_uid?: unknown }).author_uid;
  delete (question as RawQuestion & { current_user_uid?: unknown }).current_user_uid;
  delete (question as RawQuestion & { full_hash?: unknown }).full_hash;
  delete (question as RawQuestion & { uid?: unknown }).uid;
  delete (question as RawQuestion & { user_free_hash?: unknown }).user_free_hash;
  return question;
}

function serializeQuestionPart(
  part: EditableQuestionPart,
  currentUserId: string,
  // True when the multi-part PARENT defines the random variables. The parent is
  // then the single source of truth and the part must not persist its own copy
  // (see the variables delete below).
  parentOwnsVariables = false,
): RawQuestion {
  const sourceQuestion = part.rawOriginal
    ? (toSnakeCaseValue(cloneValue(part.rawOriginal)) as RawQuestion)
    : ({} as RawQuestion);
  const sourceContentSection =
    (sourceQuestion.content_section ?? sourceQuestion.contentSection) as
      | RawContentSection
      | null
      | undefined;
  const content = part.contentHtml ?? '<p></p>';

  const question: RawQuestion = {
    ...sourceQuestion,
    answer_units: normalizeString(part.answerUnits),
    content,
    content_section: buildTextContentSection(
      content,
      sourceContentSection,
      part.promptImages ?? [],
    ),
    current_user_uid: currentUserId,
    detailed_answer: buildOptionalTextContentSection(
      part.detailedAnswerHtml,
      (sourceQuestion.detailed_answer ?? sourceQuestion.detailedAnswer) as
        | RawContentSection
        | undefined,
      part.detailedAnswerImages ?? [],
    ),
    exact_answer: normalizeString(part.exactAnswer),
    hint: buildOptionalTextContentSection(
      part.hintHtml,
      sourceQuestion.hint as RawContentSection | undefined,
      part.hintImages ?? [],
    ),
    type: getQuestionTypeEnum(part.type),
    value: part.value,
    variable_based_answer: normalizeString(part.variableBasedAnswer),
  };

  // When the parent owns the variable definitions, drop the copy that rode
  // along on the sourceQuestion spread: a stale per-part duplicate silently
  // overrode the instructor's edited ranges for students. Parts keep only
  // variable_based_answer and inherit the parent's array on read. (Legacy
  // per-part authoring — a parent with no variables — keeps its part arrays;
  // the AI_UPLOAD branch below re-writes its explicit empty list.)
  if (parentOwnsVariables) {
    delete (question as RawQuestion & { variables?: unknown }).variables;
  }

  if (part.type === 'MULTIPLE_CHOICE' || part.type === 'SELECT_ALL') {
    question.answer_index =
      part.type === 'SELECT_ALL'
        ? [...(part.correctAnswerIndices ?? [])].sort((a, b) => a - b)
        : part.correctAnswerIndex === undefined
          ? []
          : [part.correctAnswerIndex];
    question.content_options = buildContentOptions(part.options, sourceQuestion);
    question.options = part.options.map((option) => option ?? '');
  } else {
    delete (question as RawQuestion & { answer_index?: unknown }).answer_index;
    delete (question as RawQuestion & { content_options?: unknown }).content_options;
    delete (question as RawQuestion & { options?: unknown }).options;
  }

  if (part.type === 'NUMERICAL_WITH_ERROR') {
    question.numerical_answer = part.numericalAnswer;
    question.numerical_error = part.numericalError;
  } else {
    // fallthrough below also clears the answer
    delete (question as RawQuestion & { numerical_answer?: unknown }).numerical_answer;
    delete (question as RawQuestion & { numerical_error?: unknown }).numerical_error;
  }

  // The mantissa/exponent split is deprecated: numericalAnswer already holds the
  // whole number (foldExtraExponent resolved it on load), so leaving the stored
  // exponent in place would multiply the instructor's value a second time.
  delete (question as RawQuestion & { numerical_answer_extra_exponent?: unknown })
    .numerical_answer_extra_exponent;
  delete (question as RawQuestion & { numericalAnswerExtraExponent?: unknown })
    .numericalAnswerExtraExponent;

  if (part.type !== 'EXACT_ANSWER') {
    delete (question as RawQuestion & { exact_answer?: unknown }).exact_answer;
  }

  if (part.type === 'AI_UPLOAD') {
    question.grading_rubric = serializeGradingRubric(part.gradingRubric);
    question.max_num_attempts = part.maxNumAttempts ?? 1;
    question.use_custom_num_attempts = true;
    question.use_random_variables = false;
    question.value = question.grading_rubric.total_score;
    delete (question as RawQuestion & { answer_index?: unknown }).answer_index;
    delete (question as RawQuestion & { answer_units?: unknown }).answer_units;
    delete (question as RawQuestion & { content_options?: unknown }).content_options;
    delete (question as RawQuestion & { exact_answer?: unknown }).exact_answer;
    delete (question as RawQuestion & { numerical_answer?: unknown }).numerical_answer;
    delete (question as RawQuestion & { numerical_error?: unknown }).numerical_error;
    delete (question as RawQuestion & { options?: unknown }).options;
    delete (question as RawQuestion & { variable_based_answer?: unknown })
      .variable_based_answer;
    question.variables = [];
  } else {
    delete (question as RawQuestion & { grading_rubric?: unknown })
      .grading_rubric;
    if (part.maxNumAttempts === undefined) {
      delete (question as RawQuestion & { max_num_attempts?: unknown })
        .max_num_attempts;
    } else {
      question.max_num_attempts = part.maxNumAttempts;
    }

    if (part.useCustomNumAttempts === undefined) {
      delete (question as RawQuestion & { use_custom_num_attempts?: unknown })
        .use_custom_num_attempts;
    } else {
      question.use_custom_num_attempts = part.useCustomNumAttempts;
    }

    // Round-trip the randomization flag (it used to be deleted here, which
    // silently de-randomized every part on save: students then saw raw ##var##
    // placeholders). Only write a definite value — a part with no flag of its
    // own must stay flag-ABSENT, because parts without variables inherit the
    // parent's randomization and an explicit false would block that. The part's
    // own variables array (when present) is preserved via the sourceQuestion
    // spread above.
    if (part.useRandomVariables !== undefined) {
      question.use_random_variables = part.useRandomVariables;
    }
  }

  return stripNestedIdentityFields(withoutViewerState(question, part.type));
}

function serializeSupportedQuestion(
  question: EditableQuestion,
  currentUserId: string,
): RawQuestion {
  const sourceQuestion = question.rawOriginal
    ? (toSnakeCaseValue(cloneValue(question.rawOriginal)) as RawQuestion)
    : ({} as RawQuestion);
  const sourceContentSection =
    (sourceQuestion.content_section ?? sourceQuestion.contentSection) as
      | RawContentSection
      | null
      | undefined;
  const content = question.contentHtml ?? '<p></p>';
  const isNewQuestion = !normalizeString(question.uid);

  const nextQuestion: RawQuestion = {
    ...sourceQuestion,
    answer_units: normalizeString(question.answerUnits),
    author_uid: isNewQuestion
      ? currentUserId
      : normalizeString(question.authorUid) ?? sourceQuestion.author_uid,
    content,
    content_section: buildTextContentSection(
      content,
      sourceContentSection,
      question.promptImages ?? [],
    ),
    credit_for_participation: question.creditForParticipation ?? false,
    current_user_uid: currentUserId,
    detailed_answer: buildOptionalTextContentSection(
      question.detailedAnswerHtml,
      (sourceQuestion.detailed_answer ?? sourceQuestion.detailedAnswer) as
        | RawContentSection
        | undefined,
      question.detailedAnswerImages ?? [],
    ),
    exact_answer: normalizeString(question.exactAnswer),
    full_hash: question.fullHash ?? sourceQuestion.full_hash,
    hint: buildOptionalTextContentSection(
      question.hintHtml,
      sourceQuestion.hint as RawContentSection | undefined,
      question.hintImages ?? [],
    ),
    image_paths: (question.imagePaths ?? [])
      .map((imagePath) => normalizeString(imagePath))
      .filter((imagePath): imagePath is string => imagePath !== undefined),
    is_public: question.isPublic ?? true,
    max_num_attempts: question.maxNumAttempts,
    numerical_answer: question.numericalAnswer,
    // Deprecated. numericalAnswer is the whole number, so any stored exponent
    // carried forward from sourceQuestion would scale it a second time. Cleared
    // rather than preserved: one save migrates the question off the split.
    numerical_answer_extra_exponent: undefined,
    numericalAnswerExtraExponent: undefined,
    numerical_error: question.numericalError,
    parent_subject: normalizeString(question.parentSubject),
    seed_for_random_variables:
      normalizeString(question.seedForRandomVariables) ?? content,
    tags: question.tags ?? [],
    type: getQuestionTypeEnum(question.type),
    uid: normalizeString(question.uid),
    user_free_hash: question.userFreeHash ?? sourceQuestion.user_free_hash,
    use_custom_num_attempts: question.useCustomNumAttempts ?? false,
    use_random_variables: question.useRandomVariables ?? false,
    value: question.value,
    variable_based_answer: normalizeString(question.variableBasedAnswer),
    variables: serializeVariables(question.variables, sourceQuestion.variables),
  };

  if (question.type === 'MULTIPLE_CHOICE' || question.type === 'SELECT_ALL') {
    // Select-all writes EVERY marked option; multiple choice stays a
    // one-element list. answer_index is repeated for both.
    nextQuestion.answer_index =
      question.type === 'SELECT_ALL'
        ? [...(question.correctAnswerIndices ?? [])].sort((a, b) => a - b)
        : question.correctAnswerIndex === undefined
          ? []
          : [question.correctAnswerIndex];
    nextQuestion.content_options = buildContentOptions(question.options, sourceQuestion);
    nextQuestion.options = question.options.map((option) => option ?? '');
  } else {
    delete (nextQuestion as RawQuestion & { answer_index?: unknown }).answer_index;
    delete (nextQuestion as RawQuestion & { content_options?: unknown }).content_options;
    delete (nextQuestion as RawQuestion & { options?: unknown }).options;
  }

  if (question.type !== 'NUMERICAL_WITH_ERROR') {
    delete (nextQuestion as RawQuestion & { numerical_answer?: unknown }).numerical_answer;
    delete (nextQuestion as RawQuestion & { numerical_error?: unknown }).numerical_error;
  }

  if (question.type !== 'EXACT_ANSWER') {
    delete (nextQuestion as RawQuestion & { exact_answer?: unknown }).exact_answer;
  }

  if (question.type === 'MULTI_PART') {
    // The parent owns the variables whenever it defines any: parts then persist
    // no duplicate of their own (which is what went stale on every edit).
    const parentOwnsVariables = (question.variables ?? []).length > 0;
    nextQuestion.question_set = {
      questions: (question.parts ?? []).map((part) =>
        serializeQuestionPart(part, currentUserId, parentOwnsVariables),
      ),
    };
    delete (nextQuestion as RawQuestion & { exact_answer?: unknown }).exact_answer;
    delete (nextQuestion as RawQuestion & { answer_index?: unknown }).answer_index;
    delete (nextQuestion as RawQuestion & { options?: unknown }).options;
    delete (nextQuestion as RawQuestion & { content_options?: unknown }).content_options;
    delete (nextQuestion as RawQuestion & { numerical_answer?: unknown }).numerical_answer;
    delete (nextQuestion as RawQuestion & { numerical_error?: unknown }).numerical_error;
    delete (nextQuestion as RawQuestion & { variable_based_answer?: unknown })
      .variable_based_answer;
    delete (nextQuestion as RawQuestion & { hint?: unknown }).hint;
    delete (nextQuestion as RawQuestion & { detailed_answer?: unknown }).detailed_answer;
  } else {
    delete (nextQuestion as RawQuestion & { question_set?: unknown }).question_set;
  }

  if (question.type === 'AI_UPLOAD') {
    nextQuestion.grading_rubric = serializeGradingRubric(question.gradingRubric);
    nextQuestion.max_num_attempts = question.maxNumAttempts ?? 1;
    nextQuestion.use_custom_num_attempts = true;
    nextQuestion.use_random_variables = false;
    nextQuestion.value = nextQuestion.grading_rubric.total_score;
    delete (nextQuestion as RawQuestion & { answer_index?: unknown }).answer_index;
    delete (nextQuestion as RawQuestion & { answer_units?: unknown }).answer_units;
    delete (nextQuestion as RawQuestion & { content_options?: unknown }).content_options;
    delete (nextQuestion as RawQuestion & { exact_answer?: unknown }).exact_answer;
    delete (nextQuestion as RawQuestion & { numerical_answer?: unknown }).numerical_answer;
    delete (nextQuestion as RawQuestion & { numerical_error?: unknown }).numerical_error;
    delete (nextQuestion as RawQuestion & { options?: unknown }).options;
    delete (nextQuestion as RawQuestion & { variable_based_answer?: unknown })
      .variable_based_answer;
    nextQuestion.variables = [];
  } else {
    delete (nextQuestion as RawQuestion & { grading_rubric?: unknown })
      .grading_rubric;
  }

  if (isNewQuestion) {
    delete (nextQuestion as RawQuestion & { uid?: unknown }).uid;
  }

  return withoutViewerState(nextQuestion, question.type);
}

function serializeQuestion(question: EditableQuestion, currentUserId: string): RawQuestion {
  if (question.type === 'UNSUPPORTED' && question.rawOriginal) {
    return dropViewerState(
      toSnakeCaseValue(cloneValue(question.rawOriginal)) as RawQuestion,
    );
  }

  return serializeSupportedQuestion(question, currentUserId);
}

export function serializeQuestionSetPayload(
  editorState: Pick<EditorState, 'preLectureQuestions' | 'questions'>,
  currentUserId: string,
  courseId: string,
): AddOrUpdateQuestionSetSavePayload {
  return {
    course_id: courseId,
    pre_lecture_questions: editorState.preLectureQuestions.map((question) =>
      serializeQuestion(question, currentUserId),
    ),
    question: editorState.questions.map((question) =>
      serializeQuestion(question, currentUserId),
    ),
    user_id: currentUserId,
  };
}
